/**
 * @namespace models
 */

import moment from 'moment'
import { getId } from 'app/helpers/entity'
import { has } from 'app/helpers/object'
import { YM_COUNTER } from '../../../app/core/constants'

/**
 * Entity entity class
 */

/**
 * EntityFactory
 * returns Entity entity class
 *
 * @class EntityFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Entity} class
 *
 * @ngdoc factory
 * @name mwApp.factory:EntityFactory
 */
/* @ngInject */
export default function EntityFactory ($http, $q, $injector, $interpolate, $rootScope, alertLiteSvc, accessParser, EntityCache, Type) {
  // use generic hasOwnProperty function
  var hasOwnProperty = Object.prototype.hasOwnProperty

  /* === Entity class === */

  /**
   * Entity class
   *
   * @class Entity
   * @classdesc Entity entity class
   * @memberof models
   * @see models
   * @param {Object}      [obj]   - proto object
   * @param {models.EntityCache} [cache] - entity cache
   * @param {Boolean}     [skinDeep=false] - if true -> copy only top-level entity fields, not recursively-deep
   * @return {models.Entity} Entity entity
   */
  function Entity (obj, cache, skinDeep) {
    obj = obj || {}
    cache = cache || new EntityCache()

    const id = getId(obj)

    // get object from cache
    const cachedObj = cache.get(id, this.constructor._name)

    // if object found in cache -> return it
    if (cachedObj) {
      return cachedObj
    }

    // if 'obj' is number and not found in cache -> return this number
    if (typeof obj === 'number') {
      // cache.unresolved() function returns Number object with saved entity type for futher resolving
      // if resolve will not succeed, there will be just Number field in Entity
      return cache.unresolved(obj, this.constructor._name)
    }

    // put itself in cache
    cache.put(id, this)

    // fill fields
    this._copy(obj, cache, skinDeep)

    // try to resolve unresolved links, if any
    if (cache.unresolved_count > 0) {
      // cache.resolve() function traverse through object, find all Numbers with entity types, saved above,
      // and tries to find this unresolved Entity in cache
      cache.resolve(this)
    }

    // do something with entity
    this._prepare()

    // entity url
    this._updateUrl()
  }

  /* === Entity instance methods & fields === */

  Entity.prototype = {

    /**
     * instance fields
     *
     * @memberof models.Entity#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String
    },

    /**
     * url template
     *
     * @memberof models.Entity#
     * @type {String}
     */
    _urlTemplate: 'api/nonexists/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Entity#
     * @type {String}
     */
    _publicUrlTemplate: 'nonexists/{{::id}}',

    /**
     * Check, if this entity is new or not
     * Checks id, and if id > 0
     *
     * @memberof models.Entity#
     * @return {Boolean} true if object is new (there is no id)
     */
    _isNew: function () {
      return !this.id
    },

    /**
     * Prepare, do something with entity
     *
     * @memberof models.Entity#
     */
    _prepare: function () {},

    /**
     * Copy fields from object to this
     *
     * @protected
     * @memberof models.Entity~
     * @param {Object}             obj     - object to copy
     * @param {models.EntityCache} [cache] - entity cache
     * @param {Boolean}            [skinDeep=false] - deep copy or just skin
     */
    _copy: function (obj, cache, skinDeep) {
      obj = obj || {}
      cache = cache || new EntityCache()
      cache.put(obj.id, this)

      for (var f in this._fields) {
        this[f] = !hasOwnProperty.call(obj, f)
          ? getDefaultValue(this._fields[f]) // initial object doesn't contain this field -> look for default value for it
          : skinDeep // initial object contain this field
            ? obj[f] // if `skinDeep` -> just copy field value
            : getValueByType(obj[f], this._fields[f], cache) // cast field value
      }
    },

    /**
     * Copy fields from object to this
     *
     * @protected
     * @memberof models.Entity~
     * @param {Object}      obj     - object to copy
     * @param {models.EntityCache} [cache] - entity cache
     */
    _weakCopy: function (obj, cache) {
      obj = obj || {}
      cache = cache || new EntityCache()
      cache.put(obj.id, this)

      for (var f in obj) {
        if (hasOwnProperty.call(obj, f) && hasOwnProperty.call(this._fields, f) && obj[f] !== null) {
          // update fields except those, that return null
          this[f] = getValueByType(obj[f], this._fields[f], cache)
        }
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.Entity#
     * @param {models.EntityCache} [cache] - entity cache
     * @return {Object} new underlying object as plain object
     */
    _object: function (cache) {
      cache = cache || new EntityCache()
      this.constructor._noCache || cache.put(this.id, this) // disable cache

      var ret = Object.create(null)
      for (var f in this._fields) {
        if (f.charAt(0) !== '$') { // treat $variables like 'private'
          ret[f] = getObjectByValue(this[f], this._fields[f], cache)
        }
      }

      return ret
    },

    /**
     * Get underlying object<br>
     * Unlike .map() for arrays, changes object, don't need to return value<br>
     * If map function is not provided - just call {@link models.Entity#_object})
     *
     * @memberof models.Entity#
     * @param {Function} [mapFunc] - map function
     * @return {Object} new underlying object, changed by mapFunc
     */
    _map: function (mapFunc) {
      var ret = this._object()
      if (typeof mapFunc === 'function') {
        mapFunc(ret)
      }
      return ret
    },

    /**
     * Save/update entity, performing PUT/POST request to backend
     *
     * @memberof models.Entity#
     * @param {Function} [mapFunc] - map function, it is possible to transform underlying object before request
     * @param {String} [customUrl] - custom url to send request
     * @param {String} [saveMsg]   - alerted message for save new entity (if 'null' -> there will no message)
     * @param {String} [updateMsg] - alerted message for update entity (if 'null' -> there will be no message)
     * @param {Boolean} [skipErrorMessage] - alerted message for update entity (if 'null' -> there will be no message)
     * @return {Promise} promise
     */
    _save: function (mapFunc, customUrl, saveMsg, updateMsg, skipErrorMessage = false) {
      var isNew = this._isNew()
      var name = this.name ? this.name + ' ' : ''
      var cls = this.constructor
      var cName = cls._publicName || cls._name

      // if there is no _url -> update it first and then call _save again
      if (!customUrl && !this._url) {
        return this._updateUrl()
          .then(() => this._save.apply(this, arguments))
      }

      if (updateMsg === undefined) {
        updateMsg = saveMsg
      }
      if (saveMsg !== null) {
        saveMsg = saveMsg || name + cName + ' added'
      }
      if (updateMsg !== null) {
        updateMsg = updateMsg || name + cName + ' updated'
      }

      // if there is access defined in entity class -> check 'create' or 'edit' access
      if (cls.access && (
        (isNew && cls.access.create && !accessParser(cls.access.create).check()) ||
            (!isNew && cls.access.edit && !accessParser(cls.access.edit).check())
      )) {
        // return error in case user doesn't have 'create' or 'edit' access
        return $q.reject('You have no permissions to ' + (isNew ? 'create' : 'edit') + ' ' + cName)
          .catch(alertLiteSvc.alertError)
      }
      return $http({
        method: isNew ? 'POST' : 'PUT',
        url: customUrl || this._url,
        data: this._map(mapFunc)
      }).then(
        response => {
          alertLiteSvc.alert(isNew ? saveMsg : updateMsg, 'success')

          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data
          if (data) {
            this._weakCopy(data)
            if (isNew && this.id) {
              return this._updateUrl()
                .then(() => response)
            }
          }

          return response
        }
      ).catch((error) => {
        const phrases = ['Intersection in VOD Set', 'Intersection in TV Show Set', 'Intersection in TV Channel Set', 'Intersection in Live event Set']
        if (typeof error.data === 'string' && phrases.some(item => error.data.includes(item))) {
          return $q.reject(error)
        }

        if (typeof error.data === 'string' && error.data.includes('Per-item price is required for Subscription id')) {
          error.type = 'ITEM_PRICE_REQUIRED'
          return $q.reject(error)
        }
        if (typeof skipErrorMessage === 'function') {
          if (skipErrorMessage(error)) {
            return $q.reject(error)
          }
        } else if (skipErrorMessage) {
          return $q.reject(error)
        }
        return alertLiteSvc.alertError(error)
      })
    },

    /**
     * Delete entity, performing DELETE request to backend
     *
     * @memberof models.Entity#
     * @param  {String}  [msg] - alerted message after delete is successful (if 'null' -> there will be no message)
     * @return {Promise} promise
     */
    _delete: function (msg) {
      var name = this.name ? this.name + ' ' : ''
      var cls = this.constructor
      var cName = cls._publicName || cls._name

      if (msg !== null) {
        msg = msg || name + cName + ' deleted'
      }

      // if there is access defined in entity class -> check 'delete' access
      if (cls.access && cls.access.delete && !accessParser(cls.access.delete).check()) {
        // return error in case user doesn't have 'delete' access
        return $q.reject('You have no permissions to delete ' + cName)
          .catch(alertLiteSvc.alertError)
      }
      const url = this.$_urlTemplate(this)

      return $http.delete(url)
        .then(alertLiteSvc.alertSuccess(msg, 'deleted'))
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Update URLs
     *
     * @memberof models.Entity#
     * @return {Promise} promise
     */
    _updateUrl: function () {
      var deferred = $q.defer()

      // bit hacky way, use timeout to postpone updating urls
      // this will fix two problems:
      //  - if we want to change url template, and doing so inside constructor, calling _updateUrl after that,
      //    this will save us from updating urls twice
      //  - if we use internal entities in urls, which are unknown at the moment of construction,
      //    this will save us from requirement to search and update urls for all underlying entities
      window.clearTimeout(this.__updateUrlTimeout)
      this.__updateUrlTimeout = window.setTimeout(
        () => {
          this._url = this.$_urlTemplate(this)
          this._url = this._url[this._url.length - 1] === '/' ? this._url.substring(0, this._url.length - 1) : this._url
          this._publicUrl = this.$_publicUrlTemplate(this)

          // public list url = public url with entity id set to null
          this._publicListUrl = this.$_publicUrlTemplate(Object.assign({}, this, { id: null }))

          // remove timeout id
          delete this.__updateUrlTimeout

          // resolve promise
          deferred.resolve()
        }, 0
      )

      return deferred.promise
    },

    /**
     * Update entity, performing GET request
     *
     * @memberof models.Entity#
     * @return {Promise} promise
     */
    _update: function () {
      // if it is new Entity without id -> just return itself
      if (this._isNew()) {
        return $q.when(this)
      }

      // if there is no _url -> update it first and then call _update again
      if (!this._url) {
        return this._updateUrl()
          .then(() => this._update())
      }

      // perform GET request to backend
      return $http.get(this._url)
        .then(
          response => {
            this._copy(has('payload')(response.data)
              ? response.data.payload
              : response.data)
            this._prepare()
            return this
          }
        )
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Clone entity object
     *
     * @memberof models.Entity#
     * @param  {Boolean} [deep=false] - perform deep copy
     * @return {models.Entity}
     */
    _clone: function (deep) {
      var entity = new this.constructor(this, null, !deep)

      // copy all urls in entity from original one, and remove update timeout
      window.clearTimeout(entity.__updateUrlTimeout)
      entity._url = this._url
      entity._publicUrl = this._publicUrl
      entity._publicListUrl = this._publicListUrl

      return entity
    },

    /**
     * Modify object
     * Useful to insert inside chain calls, e.g.
     *   entity
     *       ._clone()
     *       ._modufy(function(entity) { ... })
     *       ._save()
     * Unlike .map() for arrays, changes object, don't need to return value<br>
     * If map function is not provided - just return this entity
     *
     * @memberof models.Entity#
     * @param {Function} [mapFunc] - map function
     * @return {models.Entity}
     */
    _modify: function (mapFunc) {
      if (typeof mapFunc === 'function') {
        mapFunc(this)
      }
      return this
    },
    _ym: function (goal) {
      if ($rootScope.enabledYM) {
        window.ym(YM_COUNTER, 'reachGoal', goal)
      }
    }

  }

  /* === Entity static methods & fields === */

  Entity._name = 'Entity'

  /**
   * Initialize entity class
   */
  Entity.init = function () {
    var proto = this.prototype

    // pre-interpolate all url templates inside prototype
    var templatesRE = /^_.*Template$/
    for (var f in proto) {
      if (hasOwnProperty.call(proto, f) && templatesRE.test(f)) {
        proto['$' + f] = $interpolate(proto[f])
      }
    }
  }

  // call init on Entity itself
  Entity.init()

  /**
   * Loads entity by URL, performing GET request
   *
   * @memberof models.Entity
   * @param  {(String|Object)} url    - entity url string<br>
   *                                    If object -> it will be scope for {@link models.Entity#_urlTemplate}
   * @param  {(String|Object)} [cast] - will be used as structure to cast response, if provided (Entity by default)
   * @param  {String}          [opt]  - if 'raw' - just return request response<br>
   *                                    if 'all' - cast as array
   * @param  {Boolean}          [withoutAccessCheck] - if true -> access check is omitted
   * @return {Promise} promise
   */
  Entity.load = function (url, cast, opt, withoutAccessCheck) {
    var accessFrom = null
    var urlTemplateFn = null

    // get url template and access object parent
    if (cast == null && this._name) {
      cast = $injector.get(this._name) // get constructor from injector by name
      urlTemplateFn = cast.prototype.$_urlTemplate
    } else
    if (typeof cast === 'string') {
      cast = $injector.get(cast) // get constructor from injector by name
      urlTemplateFn = cast.prototype.$_urlTemplate
    } else
    if (cast.prototype && cast.prototype.$_urlTemplate) {
      urlTemplateFn = cast.prototype.$_urlTemplate
    } else
    if (this.prototype && this.prototype.$_urlTemplate) {
      urlTemplateFn = this.prototype.$_urlTemplate
      accessFrom = this // eslint-disable-line consistent-this
    }
    accessFrom = accessFrom || cast

    // if there is access defined in entity class -> check 'view' access
    if (!withoutAccessCheck &&
        accessFrom._name && accessFrom.access && accessFrom.access.view &&
        !accessParser(accessFrom.access.view).check()) {
      // return null in case user doesn't have 'view' access
      return $q.when(null)
    }

    var realUrl = url
    var config = null

    if (urlTemplateFn) {
      var urlScope = url !== null && typeof url === 'object' ? url : {}

      // get config from url scope
      if (urlScope.config) {
        config = urlScope.config
        urlScope.config = null
      }

      // get params from url scope
      if (urlScope.params) {
        config = {
          params: urlScope.params
        }
        urlScope.params = null
      }

      // get relative url (or another absolute) from url scope
      if (urlScope.url) {
        url = urlScope.url
        urlScope.url = null
      }

      // compile real url
      realUrl = urlTemplateFn(urlScope)
      realUrl = realUrl[realUrl.length - 1] === '/' ? realUrl.substring(0, realUrl.length - 1) : realUrl
      // modify real url
      if (typeof url === 'string') {
        if (url.charAt(0) === '?') {
          realUrl += url
        } else
        if (url.charAt(0) === '\\') { // use \ instead of / to recognize addable parameters vs full url
          realUrl += (realUrl.slice(-1) === '/' ? '' : '/') + url.slice(1)
        } else {
          realUrl = url
        }
      }
    }

    return $http.get(realUrl, config)
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          if (opt === 'raw') {
            return data
          }
          var cache = new EntityCache()
          var ret = getValueByType(data, opt === 'all' ? [cast] : cast, cache)
          // resolve unresolved
          if (cache.unresolved_count > 0) {
            cache.resolve(ret)
          }
          return ret
        }
      )
  }

  /**
   * Loads raw data by url
   *
   * @memberof models.Entity
   * @param  {(String|Object)} url  - entity url string<br>
   *                                  If object -> it will be scope for {@link models.Entity#_urlTemplate}
   * @param  {(String|Object)} cast - class or string name of class, not structure!
   * @return {Promise} promise
   */
  Entity.loadRaw = function (url, cast) {
    return this.load(url, cast, 'raw')
  }

  /**
   * Load all entities by url
   *
   * @memberof models.Entity
   * @param  {String|Object} url  - entity url string<br>
   *                                If object -> it will be scope for {@link models.Entity#_urlTemplate}<br>
   *                                'params' key will be added as GET params after '?' sign<br>
   *                                If starts with '?' sign -> will be added to {@link models.Entity#_urlTemplate} (on empty scope)
   * @param  {String|Object} cast - structure, will be encapsulated in array
   * @return {Promise} promise
   */
  Entity.loadAll = function (url, cast) {
    return this.load(url, cast, 'all')
  }

  /**
   * Load entitie by url without access check
   *
   * @memberof models.Entity
   * @param  {String|Object} url  - entity url string<br>
   *                                If object -> it will be scope for {@link models.Entity#_urlTemplate}<br>
   *                                'params' key will be added as GET params after '?' sign<br>
   *                                If starts with '?' sign -> will be added to {@link models.Entity#_urlTemplate} (on empty scope)
   * @param  {String|Object} cast - structure, will be encapsulated in array
   * @return {Promise} promise
   */
  Entity.loadPublic = function (url, cast) {
    return this.load(url, cast, undefined, true)
  }

  /**
   * Load extra for entity
   *
   * @memberof models.Entity
   * @param  {String|Object} url  - entity url string<br>
   * @return {Promise} promise
   */
  Entity.loadExtra = function (url) {
    switch (url === null ? 'undefined' : typeof url) {
      case 'undefined':
        url = '\\extra'
        break

      case 'string':
        url = '\\extra' + url
        break

      case 'object':
        url.url = '\\extra' + (url.url ? url.url : '')
        break
    }

    var loadPromise = this.extra
      ? this.load(url, this.extra)
      : this.load(url, undefined, 'raw')

    return loadPromise
      .catch(
        (rejection) => {
          // show error message only if there is some message inside rejection
          rejection.data && rejection.status !== 404 && alertLiteSvc.alert('Failed to load extra data for that page', 'danger')

          // return object, not promise rejection!
          // therefore any call to loadExtra will get successfully resolved promise and never rejection
          return this.extra
            ? getValueByType({}, this.extra)
            : {}
        }
      )
  }

  /**
   * Return object, copy of entity, but with id=0, if id is null in original entity
   *
   * @param  {models.Entity} o
   * @return {Object}
   */
  Entity.extraFor = function (o) {
    var ret = Object.assign({}, o)
    ret.id = ret.id || 0
    return ret
  }

  /**
   * Load entities for SmartTable <br>
   * SmartTable state looks like this:
   * {
   *   "sort": {
   *     "predicate": "channelNumber",
   *     "reverse":false
   *   },
   *   "search": {
   *     "predicateObject": {
   *       "$": "test",
   *       "platform": "123"
   *     }
   *   },
   *   "pagination": {
   *     "start": 0,
   *     "totalItemCount": 0,
   *     "number": 5,
   *     "numberOfPages": 367
   *   }
   * }
   *
   * @memberof models.Entity
   * @param {Object} tableState - SmartTable state
   * @param {Object} [o]        - object over which Url will be interpolated
   * @param {String | null} path - custom path for request
   * @return {Promise} promise
   */
  Entity.loadSmart = function (tableState, o, path = null) {
    o = o || {}

    var cast = $injector.get(this._name) // get constructor from injector by name
    var url = path || cast.prototype.$_urlTemplate(o)

    // if there is access defined in entity class -> check 'view' access
    if (cast._name && cast.access && cast.access.view && !accessParser(cast.access.view).check()) {
      // return empty array for smart table in case user doesn't have 'view' access
      return $q.when({ content: [] })
    }

    // {"sort":{"predicate":"channelNumber","reverse":false},"search":{},"pagination":{"start":0,"totalItemCount":0}}
    // sort-by=channelNumber&sort-order=asc&page=1&count=25&type=TV
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      if (tableState.sort.fieldName) {
        params[tableState.sort.fieldName] = params.$
        delete params.q
      }
      delete params.$
    }
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    if (tableState.mappingParams) {
      for (const paramsKey in tableState.mappingParams) {
        params[tableState.mappingParams[paramsKey]] = params[paramsKey]
        delete params[paramsKey]
      }
    }

    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          var result = getValueByType(data, {
            // response structure
            content: [cast._name],
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            var last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements
          tableState.pagination.last = result.last

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  /**
   * Load entities for SmartTable with options <br>
   * SmartTable state looks like this:
   * {
   *   "sort": {
   *     "predicate": "channelNumber",
   *     "reverse":false
   *   },
   *   "search": {
   *     "predicateObject": {
   *       "$": "test",
   *       "platform": "123"
   *     }
   *   },
   *   "pagination": {
   *     "start": 0,
   *     "totalItemCount": 0,
   *     "number": 5,
   *     "numberOfPages": 367
   *   }
   * }
   *
   * @memberof models.Entity
   * @param {Object} tableState - SmartTable state
   * @param {Object} [o]        - object over which Url will be interpolated
   * @param {Object} [castOptions]
   * @return {Promise} promise
   */
  Entity.loadSmartOptions = function (tableState, o, castOptions) {
    o = o || {}

    const emptyData = { content: { content: [] } }

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    // if there is access defined in entity class -> check 'view' access
    if (cast._name && cast.access && cast.access.view && !accessParser(cast.access.view).check()) {
      // return empty array for smart table in case user doesn't have 'view' access
      return $q.when(emptyData)
    }

    // {"sort":{"predicate":"channelNumber","reverse":false},"search":{},"pagination":{"start":0,"totalItemCount":0}}
    // sort-by=channelNumber&sort-order=asc&page=1&count=25&type=TV
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      delete params.$
    }

    return $http.get(url, { params })
      .data()
      .then(
        (data) => {
          if (!data) {
            return emptyData
          }

          // get and cast data
          data.content = data.content || {}
          data.content.content = data.content.content ? getValueByType(data.content.content, [cast._name]) : []
          data.options = data.options ? getValueByType(data.options, castOptions) : {}

          // change SmartTable state
          if (data.content.sort && data.content.sort.length) {
            var last = data.content.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = data.content.number * data.content.size // first element
          tableState.pagination.number = data.content.size // page size
          tableState.pagination.numberOfPages = data.content.totalPages
          tableState.pagination.totalItemCount = data.content.totalElements

          // return array for SmartTable
          return data
        }
      ).catch(alertLiteSvc.alertError)
  }

  Entity.loadContentSets = function (ids, tableState) {
    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = `${cast.prototype.$_subscriptionUrlTemplate(ids)}`
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    const params = {
      page: tableState.page || 0,
      count: tableState.count,
      name: tableState.name,
      q: tableState.q
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          for (const item of data.content) {
            item.networkId = ids.networkId
            if (item.type === 'TV') {
              item.type = 'TV_CHANNEL_SET'
            }
          }

          var result = this.cast(data, {
            // response structure
            content: [cast._name],
            number: 0,
            size: 0,
            first: true,
            last: true
          })

          tableState.page = result.number
          tableState.count = result.size // page size

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  Entity.loadCustomPrices = function (ids, tableState) {
    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = `${cast.prototype.$_customPricesUrlTemplate(ids)}`
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    const params = {
      page: tableState.page || 0,
      count: tableState.count,
      name: tableState.name
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          var result = this.cast(data, {
            // response structure
            content: [],
            number: 0,
            size: 0,
            first: true,
            last: true
          })

          tableState.page = result.number
          tableState.count = result.size // page size

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  /**
   * Cast object to structure
   *
   * @function
   * @memberof models.Entity
   * @param {Object} obj  - object to cast
   * @param {Object} cast - cast structure
   * @param {models.EntityCache} [cache] - entity cache
   * @return {Object} casted object
   */
  Entity.cast = getValueByType

  /**
   * Cast structure to object
   *
   * @function
   * @memberof models.Entity
   * @param {Object} obj  - object to cast
   * @param {Object} cast - cast structure
   * @param {models.EntityCache} [cache] - entity cache
   * @return {Object} casted object
   */
  Entity.uncast = getObjectByValue

  return Entity

  /* === Private helper functions === */

  /**
   * Helper to get object type
   * like 'typeof' but more informative
   *
   * @private
   * @memberof mwApp.factory:EntityFactory
   * @param  {*} obj       - object with unknown type
   * @param  {*} [toCast] - object with type, which first object is supposed to have
   * @return {String} object type
   */
  function getType (obj, toCast) {
    var type = typeof obj

    // clarify 'object' type
    if (type === 'object' && obj !== null) {
      if (obj instanceof Entity) {
        type = 'entity'
      } else
      if (obj instanceof Type) {
        type = 'type'
      } else
      if (obj instanceof Date) {
        type = 'date'
      } else
      if (obj instanceof moment) {
        type = 'moment'
      } else
      if (Array.isArray(obj) && obj.length > 0) {
        type = 'array'
      } else
      if (Object.prototype.toString.call(obj) === '[object Object]') {
        type = 'plain object'
      }
    } else

    // check, if type defined as constructor function
    if (type === 'function') {
      type = (obj === Number ? 'number'
        : (obj === Object ? 'plain object'
          : (obj === String ? 'string'
            : (obj === Boolean ? 'boolean'
              : (obj === RegExp ? 'regexp'
                : (obj === Date ? 'date'
                  : (obj === moment ? 'moment' : type)))))))
    }

    // check toCast, if exists, and change type, if necessary
    if (toCast &&
        ((type === 'array' && !Array.isArray(toCast)) ||
         (type === 'plain object' && Object.prototype.toString.call(toCast) !== '[object Object]'))) {
      type = typeof obj
    }
    return type
  }

  /**
   * Return field default value, according field definition
   *
   * @private
   * @memberof mwApp.factory:EntityFactory
   * @param  {*} TypedValue - value with field definition
   * @return {*}
   */
  function getDefaultValue (TypedValue) {
    switch (typeof TypedValue) {
      // simple type -> default value from model itself
      case 'number':
      case 'boolean':
        return TypedValue

      // object
      case 'object':
        // Type object -> default value from Type object
        if (TypedValue instanceof Type) {
          return TypedValue.getDefault()
        }

        // array -> empty array
        if (Array.isArray(TypedValue)) {
          return []
        }

        // otherwise -> null
        return null

      // string -> string itself or null
      case 'string':
        // if there is no such entity class name -> just use this string as default value, otherwise default value is null
        // if you need to use default value same as entity class name, use Type function,
        // for example Type(String('VOD')) or Type('VOD') (but second example is confusing, so don't use Type this way)
        return !$injector.has(TypedValue) ? TypedValue : null
    }

    // otherwise -> null
    return null
  }

  /**
   * Return field with type casting
   *
   * @private
   * @memberof mwApp.factory:EntityFactory
   * @param  {*} value      - value
   * @param  {*} TypedValue - value with type, which first value is supposed to have
   * @param  {models.EntityCache} [cache] - entity cache
   * @return {*}
   */
  function getValueByType (value, TypedValue, cache) {
    cache = cache || new EntityCache()

    /**
     * Create object by calling constructor using .apply()
     * https://stackoverflow.com/questions/1606797/use-of-apply-with-new-operator-is-this-possible
     *
     * @param {Function} Cls  - constructor
     * @param {Object[]} args - array with constructor arguments
     * @return {Object}
     */
    function newCall (Cls, args) {
      return new (Function.prototype.bind.apply(Cls, [null].concat(args)))()
    }

    var type = getType(TypedValue, value)
    var ret, i

    // for arrays we handle null values explicitly, arrays are never nulls
    if (value === null && type !== 'array') {
      return null
    }

    // if this is already Entity -> just return it, do not cast again
    if (value instanceof Entity &&
        ((type === 'string' && value.constructor._name === TypedValue) ||
         (type === 'function' && value.constructor === TypedValue))) {
      cache.put(value.id, value) // put Entity to cache!
      return value
    }

    switch (type) {
      case 'function':
        // guess it is another entity class
        if (TypedValue._name && typeof value === 'object') {
          return new TypedValue(value, cache)
        }

        // otherwise call function as constructor with 'value' as parameter
        return new TypedValue(value)
      case 'regexp':
        return newCall(TypedValue, value)
      case 'date':
        // use moment.js to parse date/time string
        return moment.utc(value).tz(moment.defaultZone.name).toDate()
      case 'moment':
        // use moment.js to parse date/time string
        // do not get underlying Date object - always use moment.js
        return moment.utc(value).tz(moment.defaultZone.name)
      case 'type':
        return TypedValue.from(value, cache)
      case 'string':
        if (TypedValue.length && $injector.has(TypedValue)) { // constructor name
          var Class = $injector.get(TypedValue) /// hack to avoid circular dependencies
          return new Class(value, cache)
        }
        return String(value)
      case 'number':
        return +value
      case 'boolean':
        return !!value
      case 'array':
        ret = []
        if (value !== null) {
          for (i = 0; i < value.length; i++) {
            ret.push(getValueByType(value[i], TypedValue[0], cache))
          }
        }
        return ret
      case 'plain object':
        ret = {}

        // Check if TypedValue is an empty object (dynamic keys like dictionary: {})
        var typedValueKeys = Object.keys(TypedValue)
        if (typedValueKeys.length === 0) {
          // For empty object definition {}, copy all properties from value as-is
          for (i in value) {
            if (hasOwnProperty.call(value, i)) {
              ret[i] = value[i]
            }
          }
        } else {
          // For defined object structure, iterate over TypedValue keys
          for (i in TypedValue) {
            // initial object contain this field -> cast it
            if (hasOwnProperty.call(value, i)) {
              ret[i] = getValueByType(value[i], TypedValue[i], cache)

            // initial object doesn't contain this field -> look for default value for it
            } else {
              ret[i] = getType(TypedValue[i]) === 'plain object'
                ? getValueByType({}, TypedValue[i], cache)
                : getDefaultValue(TypedValue[i])
            }
          }
        }
        return ret
    }

    return value
  }

  /**
   * Return plain object or plain type item by value
   *
   * @private
   * @memberof mwApp.factory:EntityFactory
   * @param  {*} value      - value
   * @param  {*} TypedValue - value with type, which first value is supposed to have
   * @param  {models.EntityCache} [cache] - entity cache
   * @return {*}
   */
  function getObjectByValue (value, TypedValue, cache) {
    if (value === null) {
      return null
    }

    var type = getType(value)
    var ttype = getType(TypedValue)
    var ret, i

    if (ttype !== 'type') {
      switch (type) {
        case 'function':
          return value()
        case 'array':
          ret = []
          for (i = 0; i < value.length; i++) {
            ret.push(getObjectByValue(value[i], TypedValue, cache))
          }
          return ret
        case 'plain object':
          ret = {}
          for (i in value) {
            if (hasOwnProperty.call(value, i)) {
              ret[i] = getObjectByValue(value[i], TypedValue, cache)
            }
          }
          return ret
        case 'entity':
          ret = cache.get(value.id, value.constructor._name)
          if (ret) {
            return ret.id
          }
          return value._object(cache)
      }
    }

    // cast value to _field type
    switch (ttype) {
      case 'regexp':
      case 'string':
        return String(value)
      case 'number':
        return +value
      case 'boolean':
        return !!value
      case 'type':
        return TypedValue.to(value, cache)
      case 'date':
        // use moment.js to format date/time string
        return moment(value).utc().format('YYYY-MM-DDTHH:mm')
      case 'moment':
        // use moment.js to format date/time string
        return (value instanceof moment ? value : moment(value)).utc()
          .format('YYYY-MM-DDTHH:mm')
    }

    return value
  }
}
